<script setup lang="ts">
import { parseISO } from 'date-fns'
import Pagination from '~/components/common/Pagination.vue'
import ReportCard from '~/components/critical-insights/ReportCard.vue'
import { getCriticalInsightsGroups } from '~/src/utils/reports/filter'
import type { TReport } from '~/src/types/reports'
import { useModalStore } from '~/stores/modal'

const props = defineProps<{ reports: TReport[]; market: string }>()

const { open, setModalParams } = useModalStore()

const data = reactive({
  groups: {},
  years: [],
})
const currentReports = ref<TReport[]>([])
const selectedUrl = ref('')

const onPageChange = (page: number) => {
  const { reports, years, groups } = getCriticalInsightsGroups({
    currentPage: page,
    currentReports: currentReports.value,
    pageSize: pageSize.value,
  })

  data.years = years
  data.groups = groups
  currentReports.value = reports
}

const { pageSize, currentPage, resetPagination, updatePage } = usePagination({
  onPageChange,
})

const openViewer = (url: string) => {
  selectedUrl.value = url
  setModalParams({ pdfUrl: url })
  open('pdfViewerModal')
}

watch(
  () => props.reports,
  (newReports) => {
    resetPagination()
    currentReports.value = [...newReports].sort(
      (a, b) => parseISO(b.date).getTime() - parseISO(a.date).getTime(),
    )
    onPageChange(currentPage.value)
  },
  { immediate: true },
)
</script>

<template>
  <div class="max-w-content-container mx-auto gap-y-4 flex flex-col">
    <div v-if="!currentReports.length" class="text-center text-xl">
      There are currently no reports published for this section, please check
      back later
    </div>

    <div
      v-if="data.years.length"
      class="gap-y-7 flex flex-col"
      data-testid="critical-insights-content"
    >
      <div class="flex flex-col gap-y-4">
        <div
          v-for="year in data.years"
          :key="year"
          class="flex flex-col gap-y-4"
        >
          <div class="bg-s-200 px-3 py-4 text-xl font-bold rounded-md">
            {{ year }} Insights
          </div>
          <div class="grid gap-3 lg:grid-cols-2">
            <ReportCard
              v-for="report in data.groups[year]"
              :key="report.ID"
              :report="report"
              :market="market"
              @open-viewer="openViewer"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-center lg:justify-start">
        <Pagination
          :page-size="pageSize"
          :total-records="currentReports.length"
          :current-page="currentPage"
          @page-change="updatePage"
        />
      </div>
    </div>
  </div>
</template>
